import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Eye, GraduationCap, Share2, Users } from 'lucide-react'
import DashboardSidebar from '../components/DashboardSidebar'
import ShareModal from '../components/ShareModal'
import { courseApi } from '../api/lessonBuilderApi'
import { genId } from '../lib/id'
import type { CourseDoc } from '../types/CourseDoc'

// Hub khoá học: danh sách khoá của giáo viên.
const MyCoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<CourseDoc[]>([])
  const [shared, setShared] = useState<CourseDoc[]>([])
  const [shareTarget, setShareTarget] = useState<CourseDoc | null>(null)
  const navigate = useNavigate()

  const refresh = () => {
    courseApi.list().then(setCourses)
    courseApi.sharedWithMe().then(setShared)
  }
  useEffect(refresh, [])

  const createCourse = async () => {
    const doc: CourseDoc = {
      id: genId('course'),
      slug: '',
      title: 'Khoá học mới',
      description: '',
      lessonIds: [],
      status: 'draft',
    }
    const saved = await courseApi.save(doc)
    navigate(`/khoa-hoc/sua/${saved.id}`)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Xoá khoá học này?')) return
    await courseApi.remove(id)
    refresh()
  }

  return (
    <div className="flex bg-[#F8FAFC]">
      <DashboardSidebar />
      <main className="flex-1 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900">Khoá học của tôi</h1>
            <p className="text-sm text-gray-500">Tạo khoá học online từ các bài giảng đã soạn</p>
          </div>
          <button
            onClick={createCourse}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700"
          >
            <Plus size={18} /> Tạo khoá học
          </button>
        </div>

        {courses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-300">
            <GraduationCap size={48} className="mb-3" />
            <p className="text-gray-400 font-medium">Chưa có khoá học nào.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                <div className="h-28 bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                  {c.coverImage ? (
                    <img src={c.coverImage} alt={c.title} className="w-full h-full object-cover" />
                  ) : (
                    <GraduationCap className="text-white/80" size={36} />
                  )}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                        c.status === 'published' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                      }`}
                    >
                      {c.status === 'published' ? 'Đã xuất bản' : 'Bản nháp'}
                    </span>
                    <span className="text-[11px] text-gray-400">{c.lessonIds.length} bài</span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">{c.title}</h3>
                  <p className="text-xs text-gray-400 mb-4 line-clamp-2 flex-1">{c.description || '—'}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigate(`/khoa-hoc/sua/${c.id}`)}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50 text-gray-700 text-sm font-semibold hover:bg-gray-100"
                    >
                      <Pencil size={15} /> Sửa
                    </button>
                    {c.status === 'published' && (
                      <button
                        onClick={() => navigate(`/khoa-hoc/${c.slug}`)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
                      >
                        <Eye size={15} /> Xem
                      </button>
                    )}
                    <button
                      onClick={() => setShareTarget(c)}
                      title="Chia sẻ"
                      className="px-3 py-2 rounded-lg text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                    >
                      <Share2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="px-3 py-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Khoá học được chia sẻ với tôi */}
        {shared.length > 0 && (
          <div className="mt-10">
            <h2 className="flex items-center gap-2 text-lg font-black text-gray-900 mb-4">
              <Users size={18} className="text-blue-600" /> Được chia sẻ với tôi
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shared.map((c) => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/khoa-hoc/${c.slug}`)}
                  className="bg-white rounded-2xl border border-blue-100 shadow-sm p-5 text-left hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                    Chia sẻ
                  </span>
                  <h3 className="text-base font-bold text-gray-900 mt-2 mb-1">{c.title}</h3>
                  <p className="text-xs text-gray-400 line-clamp-2">{c.description || '—'}</p>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {shareTarget && (
        <ShareModal
          resourceType="course"
          resourceId={shareTarget.id}
          resourceTitle={shareTarget.title}
          onClose={() => setShareTarget(null)}
        />
      )}
    </div>
  )
}

export default MyCoursesPage
